import { CreateCodeArguments } from '../../components/Homepage/CreateFlowcodeForm/CreateFlowcodeFormDesktop/CreateFlowcodeFormDesktop.hooks'
import { GENERATOR_FORM_CACHE_ENDPOINT } from '@dtx-company/true-common/src/constants/endpoints'
import { LandingPageDestination } from '@dtx-company/inter-app/src/types/flowcode'
import { Routes } from '@dtx-company/true-common/src/constants/routes'
import { getFile } from '../getFile'
import { logger } from '@dtx-company/logger'
import { setGeneratorFormCache } from './setGeneratorFormCache'
import { useRouter } from 'next/router'
import { v4 } from 'uuid'
import React, { useCallback, useContext, useRef } from 'react'

const GeneratorCacheKeyContext = React.createContext<string | undefined>(undefined)

export const GeneratorCacheKeyProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const isFreeQrCodeGenerator = useRouter().pathname === Routes.FREE_QR_CODE_GENERATOR
  const uuid = useRef(v4()).current
  //we only want to add the generator cache key to the google signin URL on the free QR code generator page
  const key = isFreeQrCodeGenerator ? uuid : undefined

  return (
    <GeneratorCacheKeyContext.Provider value={key}>{children}</GeneratorCacheKeyContext.Provider>
  )
}

export function useGeneratorCacheKey(): string | undefined {
  return useContext(GeneratorCacheKeyContext)
}

type SetGeneratorFormCache = (formValues: CreateCodeArguments) => Promise<void>

export function useSetGeneratorFormCache(): SetGeneratorFormCache {
  const cacheKey = useGeneratorCacheKey()
  return useCallback<SetGeneratorFormCache>(
    async formValues => {
      if (cacheKey) {
        await setGeneratorFormCache({ formValues, cacheKey })
      }
    },
    [cacheKey]
  )
}

export const getFilesFromTempBucket = async (
  cacheKey: string
): Promise<{ file: File | null; logo: File | null }> => {
  try {
    const res = await fetch(`${GENERATOR_FORM_CACHE_ENDPOINT}/${cacheKey}`)
    if (res.ok) {
      const { fileURL, logoURL }: { fileURL?: string; logoURL?: string } = await res.json()

      const [file, logo] = await Promise.all([
        fileURL ? getFile(fileURL) : null,
        logoURL ? getFile(logoURL) : null
      ])

      return { file, logo }
    }
    if (!res.ok) {
      await logger.logFetchError(res, { technicalArea: 'authentication' })
    }
    return { file: null, logo: null }
  } catch (e) {
    logger.logError(e, { technicalArea: 'authentication' })
    throw e
  }
}

export async function getGeneratorFormCache(
  cacheKey: string
): Promise<CreateCodeArguments | undefined> {
  try {
    const params = new URLSearchParams()
    params.append('cacheKey', cacheKey)
    const res = await fetch(`${GENERATOR_FORM_CACHE_ENDPOINT}?${params.toString()}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-type': 'application/json'
      }
    })
    if (res.ok) {
      const { value } = (await res.json()) as { value: CreateCodeArguments }

      const { file, logo } = await getFilesFromTempBucket(cacheKey)

      if (value.selectedDestination.id === LandingPageDestination.FILE) {
        value.destinationFormValues.file = file
      }
      value.centerLogoImageFile = logo
      return value
    }
    if (!res.ok) {
      await logger.logFetchError(res, { technicalArea: 'authentication' })
    }
    return undefined
  } catch (e) {
    logger.logError(e, { technicalArea: 'authentication' })
  }
}

export async function clearGeneratorFormCache(cacheKey: string): Promise<boolean> {
  try {
    const params = new URLSearchParams()
    params.set('cacheKey', cacheKey)
    const res = await fetch(`${GENERATOR_FORM_CACHE_ENDPOINT}?${params.toString()}`, {
      method: 'DELETE',
      credentials: 'include'
    })

    if (!res.ok) {
      await logger.logFetchError(res, { technicalArea: 'authentication' })
    }

    return res.ok
  } catch (e) {
    logger.logError(e, { technicalArea: 'authentication' })
    return false
  }
}
